import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/apiClient";
import { Input } from "./ui/input";

/**
 * Búsqueda global admin — Fase 10 Track B.4.
 * Input con debounce → GET /api/admin/search?q=...
 * Resultados como dropdown con casos / usuarios / comparables.
 */
export default function AdminGlobalSearch() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState({ cases: [], users: [], comparables: [] });
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults({ cases: [], users: [], comparables: [] });
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/search?q=${encodeURIComponent(q.trim())}`);
        setResults(data);
        setOpen(true);
      } catch {
        setResults({ cases: [], users: [], comparables: [] });
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const total = results.cases.length + results.users.length + results.comparables.length;

  return (
    <div ref={containerRef} className="relative w-full max-w-sm" data-testid="admin-global-search">
      <Input
        type="text"
        placeholder="Buscar casos, usuarios, comparables…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => q.trim().length >= 2 && setOpen(true)}
        className="rounded-sm h-9 text-sm"
        data-testid="admin-search-input"
      />
      {open && q.trim().length >= 2 && (
        <div className="absolute right-0 left-0 top-full mt-1 z-50 border border-gray-300 bg-white shadow-lg max-h-[480px] overflow-y-auto" data-testid="admin-search-dropdown">
          {loading && <div className="px-3 py-2 text-xs text-gray-500">Buscando…</div>}
          {!loading && total === 0 && <div className="px-3 py-3 text-xs text-gray-500" data-testid="admin-search-empty">Sin resultados.</div>}

          {results.cases.length > 0 && (
            <div>
              <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-200 text-[10px] uppercase tracking-widest text-gray-500">Casos ({results.cases.length})</div>
              {results.cases.map((c) => (
                <Link key={c.id} to={`/admin/cases/${c.id}`} onClick={() => setOpen(false)} className="block px-3 py-2 text-xs hover:bg-gray-50 border-b border-gray-100" data-testid={`search-case-${c.id}`}>
                  <div className="text-gray-900 truncate">{c.title}</div>
                  <div className="text-gray-500">{c.user_email || c.id.slice(0, 8)} · {c.state}</div>
                </Link>
              ))}
            </div>
          )}

          {results.users.length > 0 && (
            <div>
              <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-200 text-[10px] uppercase tracking-widest text-gray-500">Usuarios ({results.users.length})</div>
              {results.users.map((u) => (
                <Link key={u.id} to={`/admin/users?q=${encodeURIComponent(u.email)}`} onClick={() => setOpen(false)} className="block px-3 py-2 text-xs hover:bg-gray-50 border-b border-gray-100" data-testid={`search-user-${u.id}`}>
                  <div className="text-gray-900 truncate">{u.email}</div>
                  <div className="text-gray-500">{u.name || "—"} · {u.role}{u.disabled_at ? " · DESHABILITADO" : ""}</div>
                </Link>
              ))}
            </div>
          )}

          {results.comparables.length > 0 && (
            <div>
              <div className="px-3 py-1.5 bg-gray-50 border-b border-gray-200 text-[10px] uppercase tracking-widest text-gray-500">Comparables ({results.comparables.length})</div>
              {results.comparables.map((cmp) => (
                <Link key={cmp.id} to={`/admin/cases/${cmp.case_id}/secop`} onClick={() => setOpen(false)} className="block px-3 py-2 text-xs hover:bg-gray-50 border-b border-gray-100" data-testid={`search-comp-${cmp.id}`}>
                  <div className="text-gray-900 truncate">{cmp.entidad}</div>
                  <div className="text-gray-500 truncate">{cmp.objeto}</div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
